import type { ReactNode, ReactElement } from 'react';

import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import arrowIosBackFill from '@iconify/icons-eva/arrow-ios-back-fill';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';

export interface LightboxModalProps {
  images?: any[];
  photoIndex?: number;
  setPhotoIndex?: (...args: any[]) => any;
  isOpen?: boolean;
  onClose?: (...args: any[]) => any;
  [key: string]: any;
}

// ----------------------------------------------------------------------

export default function LightboxModal({
  images = [],
  photoIndex = 0,
  setPhotoIndex,
  isOpen,
  onClose,
}: LightboxModalProps) {
  if (!images.length || photoIndex < 0 || photoIndex >= images.length) {
    return null;
  }

  // Normalize images to get src string
  const getImageSrc = (img) => (typeof img === 'string' ? img : img?.src);

  const currentImage = getImageSrc(images[photoIndex]);
  // Transparent PNGs (e.g. signatures) need a backing colour on the dark scrim.
  const currentBgcolor =
    typeof images[photoIndex] === 'string'
      ? undefined
      : images[photoIndex]?.bgcolor;

  if (!currentImage) {
    return null;
  }

  const hasMultiple = images.length > 1;

  const handlePrev = () => {
    setPhotoIndex?.((photoIndex + images.length - 1) % images.length);
  };

  const handleNext = () => {
    setPhotoIndex?.((photoIndex + 1) % images.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose?.();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: 'rgba(22, 28, 36, 0.92)',
            backdropFilter: 'blur(8px)',
          },
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          color: 'white',
          bgcolor: 'rgba(0,0,0,0.5)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
        }}
      >
        <Icon icon={closeFill} width={24} height={24} />
      </IconButton>

      {/* Counter */}
      {hasMultiple && (
        <Typography
          variant="subtitle2"
          sx={{
            position: 'fixed',
            top: 20,
            left: 20,
            color: 'white',
          }}
        >
          {photoIndex + 1} / {images.length}
        </Typography>
      )}

      {/* Previous button */}
      {hasMultiple && (
        <IconButton
          onClick={handlePrev}
          sx={{
            position: 'fixed',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <Icon icon={arrowIosBackFill} width={24} height={24} />
        </IconButton>
      )}

      {/* Image */}
      <Box
        component="img"
        src={currentImage}
        alt=""
        sx={{
          maxWidth: '90vw',
          maxHeight: '90vh',
          objectFit: 'contain',
          borderRadius: 1,
          ...(currentBgcolor ? { bgcolor: currentBgcolor, p: 2 } : {}),
        }}
      />

      {/* Next button */}
      {hasMultiple && (
        <IconButton
          onClick={handleNext}
          sx={{
            position: 'fixed',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <Icon icon={arrowIosForwardFill} width={24} height={24} />
        </IconButton>
      )}
    </Dialog>
  );
}
